import React, { useState } from 'react';
import { Button, Checkbox, FormControlLabel, Grid, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

import { NumericField } from '@paragon/material';
import { InlineNotification } from '@paragon/notification-tools';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { assertExhaustiveSwitch, naturalSort, naturalSortByProperty } from '@paragon/utilities';

import { generateQualityControlPdfForActiveComponentDesign } from '../../store/actions/downloadActions';
import { getActiveAnalysisSet } from '../../store/selectors/getActiveAnalysisSetSelector';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { useAppDispatch, useAppSelector } from '../hooks';

type SelectionType = 'selectPlates' | 'specifyJsi';
type PlateSelection = {
  [plateGuid: string]: boolean;
};

export function QualityControlPdfDownloadDialog() {
  const dispatch = useAppDispatch();
  const activeComponentDesign = useAppSelector(getActiveComponentDesign);
  const activeAnalysisSet = useAppSelector(getActiveAnalysisSet);

  const allowSpecifyJsi = !!activeAnalysisSet?.jsiByJoint;

  const [selectionType, setSelectionType] = useState<SelectionType>(allowSpecifyJsi ? 'specifyJsi' : 'selectPlates');
  const [jsiMinimum, setJsiMinimum] = useState(0.8);
  const platePairs = activeComponentDesign?.component.platePairs;
  const getDefaultPlatePairSelectionState = (selected: boolean) =>
    Object.fromEntries(platePairs?.map((platePair) => [platePair.guid, selected]) ?? []);
  const [plateSelection, setPlatePairSelection] = useState<PlateSelection>(getDefaultPlatePairSelectionState(false));

  const jointsAtOrAboveJsiMinimum = activeAnalysisSet?.jsiByJoint
    ?.filter((jsiAndJoint) => jsiAndJoint.jsi >= jsiMinimum)
    .map((jsiAndJoint) => jsiAndJoint.joint)
    .toSorted(naturalSort)
    .join(', ');

  const downloadQualityControlPdf = () => {
    let jsiThresholdOrPlateGuids: number | string[];
    switch (selectionType) {
      case 'selectPlates': {
        jsiThresholdOrPlateGuids = Object.entries(plateSelection)
          .filter(([_platePairGuid, selected]) => selected)
          .map(([platePairGuid, _selected]) => platePairGuid);
        break;
      }
      case 'specifyJsi': {
        jsiThresholdOrPlateGuids = jsiMinimum;
        break;
      }
      default:
        assertExhaustiveSwitch(selectionType);
    }
    dispatch(closeCurrentPopup());
    dispatch(generateQualityControlPdfForActiveComponentDesign(jsiThresholdOrPlateGuids));
  };

  const analysisSetOverCapacity = (activeAnalysisSet?.capacity ?? 0) > 1;
  const submitDisabled =
    analysisSetOverCapacity ||
    (selectionType === 'selectPlates' && !Object.values(plateSelection).some((selected) => selected)) ||
    (selectionType === 'specifyJsi' && !jointsAtOrAboveJsiMinimum);

  return (
    <Dialog
      title="Quality Control PDF Download"
      submitText="download"
      variant="design"
      onSubmit={downloadQualityControlPdf}
      submitDisabled={submitDisabled}
      onCancel={() => dispatch(closeCurrentPopup())}
      contentSx={{ width: '400px', height: '400px' }}
    >
      <ToggleButtonGroup
        value={selectionType}
        exclusive
        onChange={(_, value) => {
          if (value != null) setSelectionType(value);
        }}
      >
        <ToggleButton value="specifyJsi" disabled={!allowSpecifyJsi}>
          Specify JSI
        </ToggleButton>
        <ToggleButton value="selectPlates">Select Plates</ToggleButton>
      </ToggleButtonGroup>
      {activeAnalysisSet ? null : (
        <InlineNotification type="error">
          An analyzed truss must be active to produce a quality control PDF
        </InlineNotification>
      )}
      {analysisSetOverCapacity && (
        <InlineNotification type="error">
          Cannot produce quality control PDF for a truss that is over capacity
        </InlineNotification>
      )}
      {selectionType === 'specifyJsi' && (
        <>
          <NumericField
            label="JSI Minimum"
            value={jsiMinimum}
            min={0}
            max={1}
            onSubmit={setJsiMinimum}
            disabled={analysisSetOverCapacity}
          />
          <Typography>Names of included joints: {jointsAtOrAboveJsiMinimum || 'none'}</Typography>
        </>
      )}
      {selectionType === 'selectPlates' && platePairs && (
        <>
          <Grid container>
            {naturalSortByProperty(platePairs, (platePair) => platePair.name).map((platePair) => (
              <Grid size={6} key={platePair.guid}>
                <FormControlLabel
                  label={platePair.name}
                  control={
                    <Checkbox
                      checked={plateSelection[platePair.guid] ?? false}
                      onChange={(event, checked) =>
                        setPlatePairSelection({ ...plateSelection, [platePair.guid]: checked })
                      }
                      disabled={analysisSetOverCapacity}
                    />
                  }
                />
              </Grid>
            ))}
          </Grid>
          <Button
            variant="outlined"
            sx={{ marginRight: 1 }}
            onClick={() => void setPlatePairSelection(getDefaultPlatePairSelectionState(true))}
          >
            Select all
          </Button>
          <Button
            variant="outlined"
            onClick={() => void setPlatePairSelection(getDefaultPlatePairSelectionState(false))}
          >
            Clear all
          </Button>
        </>
      )}
    </Dialog>
  );
}
